import React, { useEffect, useRef, useState } from "react";
import { DialogContainer } from "react-md";
import EventService from "../../../../../../service/event/EventService";
import CartDashboardUtils from "../../services/CartDashboardUtils";

/**
 * Read-only "Cart rejected!" details dialog shown from the dashboard when the
 * reviewer clicks a Rejected row's status pill. Renders the stored reject
 * reason (resolved to a label via GET /admin/reject-reasons by id) plus the
 * free-text description.
 *
 *  - View cart → onViewCart() (parent opens the cart read-only / VIEW-ONLY).
 *  - Close → onClose().
 *
 * The cart row already carries `rejectReasonId` and `rejectReasonDescription`.
 * The label is fetched lazily on first open and memoised on the component so
 * re-opens are instant.
 *
 * The lookup asks for inactive entries too: this is history, and the reason a
 * cart was rejected under may since have been retired by ops. Resolving
 * against the active-only list would silently blank out the label on exactly
 * the older carts this dialog exists to explain.
 */
const CartRejectedDialog = ({ visible, cart, onClose, onViewCart }) => {
  const cartDashboardUtils = useRef(new CartDashboardUtils()).current;
  const [reasons, setReasons] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!visible || reasons != null) return;
    setLoading(true);
    cartDashboardUtils.fetchRejectReasons(
      true,
      (res) => {
        setReasons(Array.isArray(res) ? res : []);
        setLoading(false);
      },
      (err) => {
        setLoading(false);
        setReasons([]);
        EventService.emit("showError", {
          message: `Error loading reject reasons: ${
            err?.message || "Unknown error"
          }`,
        });
      },
    );
  }, [visible]);

  const reasonLabel = (() => {
    const id = cart?.rejectReasonId;
    if (!id) return "-";
    if (!Array.isArray(reasons)) return loading ? "Loading…" : "-";
    const match = reasons.find((r) => r.id === id);
    return match?.label || "-";
  })();
  const description = cart?.rejectReasonDescription || "-";

  return (
    <DialogContainer
      id="cart-rejected-dialog"
      visible={visible}
      onHide={onClose}
      focusOnMount={false}
      width={"560px"}
      autosizeContent={true}
      dialogStyle={{ borderRadius: "10px" }}
      modal
    >
      <div className="crd-container">
        <div className="crd-header">
          <h2 className="crd-title">Cart rejected!</h2>
        </div>

        <div className="crd-body">
          <div className="crd-label">Reason for rejection</div>
          <div className="crd-readonly">{reasonLabel}</div>

          <div className="crd-label" style={{ marginTop: "20px" }}>
            Please describe the reason for rejection
          </div>
          <div className="crd-readonly crd-readonly--multiline">
            {description}
          </div>
        </div>

        <div className="crd-footer">
          <button
            type="button"
            className="crd-btn crd-btn--primary"
            onClick={() => {
              // Parent opens the cart read-only (VIEW-ONLY).
              if (onViewCart) onViewCart();
            }}
          >
            View cart
          </button>
          <button
            type="button"
            className="crd-btn crd-btn--secondary"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </DialogContainer>
  );
};

export default CartRejectedDialog;
